import { Link } from 'react-router-dom';
import { Heading } from '@clay-run/glaze/heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { costPer, count, metaResults, usd } from '../format';
import type { MetaMetrics } from '../types';
import { MetaStatusBadge } from './CampaignStatusBadge';

export interface CampaignRow {
  id: string;
  name: string;
  subtitle: string;
  status: string;
  dailyBudgetUsd: number | null;
  metrics: MetaMetrics;
  href: string;
}

/** One ad account's campaigns on the Performance page, whichever platform it is. */
export function PlatformCampaignsSection({
  title,
  accountLabel,
  note,
  loading,
  rows,
}: {
  title: string;
  accountLabel: string | null;
  note: React.ReactNode;
  loading: boolean;
  rows: CampaignRow[] | undefined;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <Heading level={2} size="md">
          {title}
        </Heading>
        {accountLabel && (
          <Text size="sm" variant="tertiary">
            {accountLabel}
          </Text>
        )}
      </div>
      {note}
      {loading && <Text variant="tertiary">Loading campaigns…</Text>}
      {rows?.length === 0 && <Text variant="tertiary">No campaigns in this ad account yet.</Text>}
      {rows && rows.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Campaign</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader align="right">Budget/day</TableHeader>
              <TableHeader align="right">Spend</TableHeader>
              <TableHeader align="right">Impressions</TableHeader>
              <TableHeader align="right">Results</TableHeader>
              <TableHeader align="right">Cost/result</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(c => {
              const results = metaResults(c.metrics);
              return (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <Link to={c.href} className="font-medium hover:underline">
                        {c.name}
                      </Link>
                      <span className="text-xs text-content-tertiary">{c.subtitle}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <MetaStatusBadge effectiveStatus={c.status} />
                  </TableCell>
                  <TableCell align="right">{c.dailyBudgetUsd === null ? '—' : usd(c.dailyBudgetUsd)}</TableCell>
                  <TableCell align="right">
                    <span className="font-mono">{usd(c.metrics.spendUsd, 2)}</span>
                  </TableCell>
                  <TableCell align="right">{count(c.metrics.impressions)}</TableCell>
                  <TableCell align="right">
                    <span className="font-mono">{count(results.value)}</span>
                    <span className="block text-xs text-content-tertiary">{results.label.toLowerCase()}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span className="font-mono">{costPer(c.metrics.spendUsd, results.value)}</span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
